/**
 * 09 帮助中心 `/help`（含证书查询指引入口；**仅指引，无下载**）
 * -----------------------------------------------------------------------------
 * 版面按设计稿「学员端 · 帮助中心」：搜索框 → 分组的常见问题（手风琴）→
 * 底部「联系在线客服」卡。
 * 设计稿的分组名（报名与资格 / 教材与学习 / 证书与查询）沿用，问题按本域重写。
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, SearchIcon } from '../components/icons';
import { Button, Card, PageShell, SectionLabel } from '../components/layout';

interface FaqItem {
  q: string;
  a: string;
}

const FAQ_GROUPS: readonly { group: string; items: readonly FaqItem[] }[] = [
  {
    group: '报名与资格',
    items: [
      {
        q: '报名需要准备哪些材料？',
        a: '身份证原件（用于识别与核验）、本人实时自拍（用于人脸核验与证件照生成）。证件照必须现拍，不接受相册图片。',
      },
      {
        q: '为什么提交前要做身份核验？',
        a: '核验是提交前的必要环节：证件识别、证件号校验、人脸比对与证件照生成全部通过后才能提交。任一环节未通过，系统会当场提示重拍或重填。',
      },
      {
        q: '报名信息填错了还能修改吗？',
        a: '草稿与已驳回的报名单可以自由修改后重新提交；已提交待缴费的单如需改敏感字段，请联系客服协助处理。',
      },
    ],
  },
  {
    group: '缴费与发票',
    items: [
      {
        q: '提交后如何缴费？',
        a: '提交成功后进入「待缴费」，在缴费指引页复制备注码并按规定金额转账，备注码必须填写在转账备注中。系统不支持站外代缴渠道。',
      },
      {
        q: '多久能核销到账？',
        a: '系统按备注码自动匹配流水，约 1~2 个工作日。未填写备注码或金额不符会转人工处理，到账确认会明显变慢。',
      },
    ],
  },
  {
    group: '教材与学习',
    items: [
      {
        q: '教材什么时候寄出？运费怎么算？',
        a: '报名成功后 3 个工作日内寄出，运费到付。选择现场领取的报名单不产生邮寄，按通知到指定地点领取。',
      },
      {
        q: '在哪里查看教材发放状态？',
        a: '在「报名详情」的「教材」区块查看发放状态（未发放 / 已发货 / 已签收 / 已领取 / 异常）。',
      },
    ],
  },
  {
    group: '证书与查询',
    items: [
      {
        q: '证书在哪里查询？能查到真伪吗？',
        a: '证书由发证机关颁发与管理，请按「证书查询指引」到发证机关官网查询，以官网结果为准。本系统不提供证书下载。',
      },
    ],
  },
];

export default function Help() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);

  const groups = useMemo(() => {
    const text = keyword.trim();
    if (!text) return FAQ_GROUPS;
    return FAQ_GROUPS.map((group) => ({
      group: group.group,
      items: group.items.filter(
        (item) => item.q.includes(text) || item.a.includes(text) || group.group.includes(text),
      ),
    })).filter((group) => group.items.length > 0);
  }, [keyword]);

  const empty = groups.length === 0;

  return (
    <PageShell title="帮助中心" back solidNav>
      <div className="search-box">
        <SearchIcon size={16} />
        <input
          className="search-box__input"
          value={keyword}
          placeholder="搜索问题，如「缴费」「教材」"
          onChange={(event) => setKeyword(event.target.value)}
        />
      </div>

      {empty ? (
        <Card tight>
          <p className="card__desc">
            没有找到「{keyword}」相关的问题，换个关键词，或直接联系在线客服。
          </p>
          <Button variant="ghost" block onClick={() => setKeyword('')}>
            清空关键词
          </Button>
        </Card>
      ) : null}

      {groups.map((group) => (
        <Card key={group.group} tight>
          <SectionLabel>{group.group}</SectionLabel>
          <div className="faq-group">
            {group.items.map((item) => {
              const key = `${group.group}|${item.q}`;
              const open = openKey === key;
              return (
                <div key={key}>
                  <button
                    type="button"
                    className={`faq-q${open ? ' faq-q--open' : ''}`}
                    aria-expanded={open}
                    onClick={() => setOpenKey(open ? null : key)}
                  >
                    <span className="faq-q__text">{item.q}</span>
                    <span className="faq-q__chev">
                      <ChevronRightIcon size={15} />
                    </span>
                  </button>
                  {open ? <div className="faq-a">{item.a}</div> : null}
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      <Card>
        <p className="card__desc">
          没有找到答案？可直接联系在线客服，工作日 9:00 - 18:00 有人承接。
        </p>
        <Button variant="primary" block onClick={() => navigate('/help/service')}>
          联系在线客服
        </Button>
      </Card>
    </PageShell>
  );
}
